import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Bookmark, BookmarkCheck, Copy, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PromptGenerator from "@/components/PromptGenerator";

export default function Prompts() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [copiedId, setCopiedId] = useState(null);
  const { user, refreshUser } = useAuth();

  useEffect(() => { http.get("/prompts").then(({ data }) => setItems(data.items)); }, []);

  const cats = ["All", ...Array.from(new Set(items.map((p) => p.category)))];
  const filtered = cat === "All" ? items : items.filter((p) => p.category === cat);

  const copy = async (p) => {
    await navigator.clipboard.writeText(p.preview);
    setCopiedId(p.title);
    toast.success("Copied.");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const toggle = async (title) => {
    if (!user) { toast.error("Sign in to save prompts."); return; }
    const saved = user.saved_prompts?.includes(title);
    try {
      await http.post(saved ? "/me/unsave" : "/me/save", { item_id: title, kind: "prompt" });
      await refreshUser();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="prompts-page">
      <div className="label-eyebrow mb-3">Prompt Library</div>
      <h1 className="text-5xl sm:text-6xl font-black tracking-tight font-display-ai max-w-3xl">
        The arsenal. <span className="text-[#00FFC2]">Always loaded.</span>
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl leading-relaxed">
        Field-tested prompts for scripts, brand voice, distribution, monetization.
        Copy, fill in, fire.
      </p>

      <section className="mt-12">
        <PromptGenerator />
      </section>

      <div className="mt-16 flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`prompt-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2 rounded-full text-xs tracking-wide border hairline ${
              cat === c ? "bg-[#00FFC2] text-black border-transparent" : "hover:bg-white/5"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => {
          const saved = user?.saved_prompts?.includes(p.title);
          return (
            <article key={p.title} className="glass rounded-xl p-5 flex flex-col" data-testid={`prompt-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#00FFC2] mb-2">{p.category}</div>
              <div className="text-lg font-semibold">{p.title}</div>
              <p className="text-sm text-gray-400 mt-3 leading-relaxed flex-1">{p.preview}</p>
              <div className="mt-4 flex items-center justify-between border-t hairline pt-3">
                <button
                  onClick={() => toggle(p.title)}
                  className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5"
                  data-testid={`prompt-save-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {saved ? <BookmarkCheck size={14} className="text-[#00FFC2]" /> : <Bookmark size={14} />}
                  {saved ? "Saved" : "Save"}
                </button>
                <button
                  onClick={() => copy(p)}
                  className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5"
                  data-testid={`prompt-copy-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {copiedId === p.title ? <Check size={14} /> : <Copy size={14} />}
                  {copiedId === p.title ? "Copied" : "Copy"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
